import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All" },
  { id: "starters", label: "Starters" },
  { id: "mains", label: "Main Courses" },
  { id: "desserts", label: "Desserts" },
  { id: "tea", label: "Tea & Drinks" },
];

const MenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        setMenuItems(response.data);
      } catch (error) {
        console.error("Error fetching menu:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const filteredItems = activeCategory === "all"
    ? menuItems
    : menuItems.filter(item => item.category === activeCategory);

  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div data-testid="menu-page" className="min-h-screen bg-[#0C0C0E] pt-24">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-saffron/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-pomegranate/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-accent text-saffron text-xl mb-3"
          >
            Discover Our
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6"
          >
            Menu
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-body text-white/50 text-lg max-w-xl"
          >
            Explore our collection of authentic Iranian dishes, crafted with love and tradition.
          </motion.p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="sticky top-20 z-40 bg-[#0C0C0E]/90 backdrop-blur-lg border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`category-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-2.5 rounded-full font-body text-sm whitespace-nowrap transition-all duration-300 ${
                  activeCategory === cat.id
                    ? "bg-saffron text-white"
                    : "bg-surface text-white/60 hover:text-white hover:bg-surface-highlight"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Items */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="space-y-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-8 bg-surface rounded w-48 mb-6" />
                  <div className="space-y-4">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="flex gap-6">
                        <div className="w-24 h-24 bg-surface rounded-xl" />
                        <div className="flex-1">
                          <div className="h-6 bg-surface rounded w-48 mb-2" />
                          <div className="h-4 bg-surface rounded w-full mb-2" />
                          <div className="h-5 bg-surface rounded w-16" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: categoryIndex * 0.1 }}
                >
                  <h2 className="font-heading text-3xl md:text-4xl text-white capitalize mb-8">
                    {category === "tea" ? "Tea & Drinks" : category}
                  </h2>
                  <div className="space-y-6">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        data-testid={`menu-item-${item.id}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="group flex gap-6 p-4 -mx-4 rounded-xl hover:bg-surface/50 transition-all duration-300"
                      >
                        {/* Image */}
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden flex-shrink-0">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-surface to-surface-highlight flex items-center justify-center">
                              <span className="font-heading text-2xl text-white/10">{item.name[0]}</span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <h3 className="font-heading text-xl md:text-2xl text-white group-hover:text-saffron transition-colors">
                              {item.name}
                            </h3>
                            <span className="font-heading text-xl md:text-2xl text-saffron flex-shrink-0">
                              ${item.price.toFixed(2)}
                            </span>
                          </div>
                          <p className="font-body text-white/50 text-sm md:text-base mt-2 line-clamp-2">
                            {item.description}
                          </p>
                          {item.is_featured && (
                            <span className="inline-block mt-3 px-3 py-1 rounded-full bg-pomegranate/20 text-pomegranate text-xs font-body">
                              Chef's Favorite
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
